import React, { useState } from 'react';
import ExpenseModal from './ExpenseModal';
import '../styles/transaction.css';

function ExpenseListModal({ person, expenses, onClose, onUpdate }) {
  const [searchTerm, setSearchTerm] = useState('');
  const [showExpenseModal, setShowExpenseModal] = useState(false);
  const [editingExpense, setEditingExpense] = useState(null);
  const [dateFilter, setDateFilter] = useState('all'); // all, week, month, custom
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');

  const getFilteredByDate = (expensesList) => {
    if (dateFilter === 'all') return expensesList;

    const now = new Date();
    return expensesList.filter(expense => {
      if (!expense.date) return false;
      
      // Parse date in MM/DD/YYYY or YYYY-MM-DD format
      let expenseDate;
      if (expense.date.includes('/')) {
        const [month, day, year] = expense.date.split('/');
        expenseDate = new Date(year, month - 1, day);
      } else {
        expenseDate = new Date(expense.date);
      }

      if (dateFilter === 'week') {
        const weekAgo = new Date(now);
        weekAgo.setDate(weekAgo.getDate() - 7);
        weekAgo.setHours(0, 0, 0, 0);
        const endOfDay = new Date(now);
        endOfDay.setHours(23, 59, 59, 999);
        return expenseDate >= weekAgo && expenseDate <= endOfDay;
      }

      if (dateFilter === 'month') {
        const monthAgo = new Date(now);
        monthAgo.setDate(monthAgo.getDate() - 30);
        monthAgo.setHours(0, 0, 0, 0);
        const endOfDay = new Date(now);
        endOfDay.setHours(23, 59, 59, 999);
        return expenseDate >= monthAgo && expenseDate <= endOfDay;
      }

      if (dateFilter === 'custom') {
        if (!customStartDate && !customEndDate) return true;
        
        const start = customStartDate ? new Date(customStartDate) : new Date(0);
        start.setHours(0, 0, 0, 0);
        
        const end = customEndDate ? new Date(customEndDate) : new Date();
        end.setHours(23, 59, 59, 999);
        
        return expenseDate >= start && expenseDate <= end;
      }

      return true;
    });
  };

  const filteredExpenses = getFilteredByDate(
    (expenses || []).filter(expense =>
      expense.description?.toLowerCase().includes(searchTerm.toLowerCase())
    )
  );

  const totalExpenses = filteredExpenses.reduce((sum, e) => sum + (e.amount || 0), 0);

  const handleExpenseClick = (expense) => {
    setEditingExpense(expense);
    setShowExpenseModal(true);
  };

  const handleAddExpense = (expense) => {
    const existingExpenses = expenses || [];
    
    let updatedExpenses;
    if (editingExpense) {
      updatedExpenses = existingExpenses.map(e => 
        e.id === editingExpense.id ? { ...expense, id: editingExpense.id } : e
      );
      setEditingExpense(null);
    } else {
      updatedExpenses = [...existingExpenses, expense];
    }
    
    onUpdate(updatedExpenses);
    setShowExpenseModal(false);
  };

  return (
    <>
      <div className="modal-overlay" onClick={onClose}>
        <div 
          className="transaction-modal" 
          onClick={(e) => e.stopPropagation()}
          style={{ maxWidth: '700px', maxHeight: '80vh', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}
        >
          <div className="modal-header">
            <h3>💸 Expenses - {person?.name || person?.code}</h3>
            <button className="modal-close" onClick={onClose}>✕</button>
          </div>

          {/* Search and Add */}
          <div style={{ padding: '20px', borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>
            <div style={{ display: 'flex', gap: '10px', marginBottom: '15px' }}>
              <input
                type="text"
                placeholder="🔍 Search expenses..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{
                  flex: 1,
                  padding: '12px',
                  borderRadius: '8px',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  background: 'rgba(255, 255, 255, 0.03)',
                  color: '#fff',
                  fontSize: '14px'
                }}
              />
              <button
                onClick={() => {
                  setEditingExpense(null);
                  setShowExpenseModal(true);
                }}
                style={{
                  padding: '12px 24px',
                  background: 'linear-gradient(135deg, #d4af37, #c4941f)',
                  border: 'none',
                  borderRadius: '8px',
                  color: '#000',
                  fontWeight: '600',
                  cursor: 'pointer',
                  whiteSpace: 'nowrap'
                }}
              >
                ➕ Add Expense
              </button>
            </div>

            {/* Date Filter */}
            <div style={{ display: 'flex', gap: '10px', marginBottom: '15px', flexWrap: 'wrap' }}>
              <button
                onClick={() => setDateFilter('all')}
                style={{
                  padding: '8px 16px',
                  background: dateFilter === 'all' ? 'rgba(212, 175, 55, 0.2)' : 'rgba(255, 255, 255, 0.05)',
                  border: dateFilter === 'all' ? '1px solid #d4af37' : '1px solid rgba(255, 255, 255, 0.1)',
                  borderRadius: '8px',
                  color: dateFilter === 'all' ? '#d4af37' : '#fff',
                  fontSize: '13px',
                  cursor: 'pointer',
                  fontWeight: dateFilter === 'all' ? '600' : '400'
                }}
              >
                📅 All Time
              </button>
              <button
                onClick={() => setDateFilter('week')}
                style={{
                  padding: '8px 16px',
                  background: dateFilter === 'week' ? 'rgba(212, 175, 55, 0.2)' : 'rgba(255, 255, 255, 0.05)',
                  border: dateFilter === 'week' ? '1px solid #d4af37' : '1px solid rgba(255, 255, 255, 0.1)',
                  borderRadius: '8px',
                  color: dateFilter === 'week' ? '#d4af37' : '#fff',
                  fontSize: '13px',
                  cursor: 'pointer',
                  fontWeight: dateFilter === 'week' ? '600' : '400'
                }}
              >
                📆 Last 7 Days
              </button>
              <button
                onClick={() => setDateFilter('month')}
                style={{
                  padding: '8px 16px',
                  background: dateFilter === 'month' ? 'rgba(212, 175, 55, 0.2)' : 'rgba(255, 255, 255, 0.05)',
                  border: dateFilter === 'month' ? '1px solid #d4af37' : '1px solid rgba(255, 255, 255, 0.1)',
                  borderRadius: '8px',
                  color: dateFilter === 'month' ? '#d4af37' : '#fff',
                  fontSize: '13px',
                  cursor: 'pointer',
                  fontWeight: dateFilter === 'month' ? '600' : '400'
                }}
              >
                📊 Last 30 Days
              </button>
              <button
                onClick={() => setDateFilter('custom')}
                style={{
                  padding: '8px 16px',
                  background: dateFilter === 'custom' ? 'rgba(212, 175, 55, 0.2)' : 'rgba(255, 255, 255, 0.05)',
                  border: dateFilter === 'custom' ? '1px solid #d4af37' : '1px solid rgba(255, 255, 255, 0.1)',
                  borderRadius: '8px',
                  color: dateFilter === 'custom' ? '#d4af37' : '#fff',
                  fontSize: '13px',
                  cursor: 'pointer',
                  fontWeight: dateFilter === 'custom' ? '600' : '400'
                }}
              >
                🎯 Custom Range
              </button>
            </div>

            {/* Custom Date Range */}
            {dateFilter === 'custom' && (
              <div style={{ display: 'flex', gap: '10px', marginBottom: '15px', alignItems: 'center' }}>
                <input
                  type="date"
                  value={customStartDate}
                  onChange={(e) => setCustomStartDate(e.target.value)}
                  style={{
                    flex: 1,
                    padding: '10px',
                    borderRadius: '8px',
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                    background: 'rgba(255, 255, 255, 0.03)',
                    color: '#fff',
                    fontSize: '13px'
                  }}
                />
                <span style={{ color: 'rgba(255, 255, 255, 0.5)' }}>to</span>
                <input
                  type="date"
                  value={customEndDate}
                  onChange={(e) => setCustomEndDate(e.target.value)}
                  style={{
                    flex: 1,
                    padding: '10px',
                    borderRadius: '8px',
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                    background: 'rgba(255, 255, 255, 0.03)',
                    color: '#fff',
                    fontSize: '13px'
                  }}
                />
              </div>
            )}
            <div style={{ 
              fontSize: '16px', 
              fontWeight: '600', 
              color: '#ff6b6b',
              textAlign: 'center'
            }}>
              Total: -${totalExpenses.toLocaleString('en-US', { minimumFractionDigits: 2 })}
            </div>
          </div>

          {/* Expenses List */}
          <div style={{ 
            flex: 1, 
            overflowY: 'auto', 
            padding: '20px'
          }}>
            {filteredExpenses.length > 0 ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {filteredExpenses
                  .sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0))
                  .map(expense => (
                    <div
                      key={expense.id}
                      onClick={() => handleExpenseClick(expense)}
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '16px',
                        background: 'rgba(255, 255, 255, 0.05)',
                        borderRadius: '8px',
                        border: '1px solid rgba(255, 255, 255, 0.05)',
                        cursor: 'pointer',
                        transition: 'all 0.2s'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = 'rgba(255, 255, 255, 0.08)';
                        e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.15)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = 'rgba(255, 255, 255, 0.05)';
                        e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.05)';
                      }}
                    >
                      <span style={{ color: '#fff', fontSize: '15px', flex: 1 }}>
                        {expense.description}
                      </span>
                      <div style={{ display: 'flex', gap: '20px', alignItems: 'center' }}>
                        <span style={{ 
                          color: '#ff6b6b', 
                          fontSize: '16px', 
                          fontWeight: '700',
                          minWidth: '100px',
                          textAlign: 'right'
                        }}>
                          -${expense.amount.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                        </span>
                        <span style={{ 
                          color: 'rgba(255, 255, 255, 0.5)', 
                          fontSize: '13px',
                          minWidth: '80px'
                        }}>
                          {expense.date}
                        </span>
                      </div>
                    </div>
                  ))}
              </div>
            ) : (
              <div style={{ 
                textAlign: 'center', 
                padding: '40px', 
                color: 'rgba(255, 255, 255, 0.5)' 
              }}>
                {searchTerm ? 'No expenses found' : 'No expenses yet'}
              </div>
            )}
          </div>
        </div>
      </div>

      {showExpenseModal && (
        <ExpenseModal
          person={person}
          onClose={() => {
            setShowExpenseModal(false);
            setEditingExpense(null);
          }}
          onSave={handleAddExpense}
          existingExpense={editingExpense}
        />
      )}
    </>
  );
}

export default ExpenseListModal;
